// eslint-disable-next-line sort-imports
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Button,
  Dialog,
  Divider,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
} from '@mui/material';

// eslint-disable-next-line sort-imports
import {
  EvolutionApiIntegrationType,
  useSaveEvolutionApiConfigMutation,
  SaveEvolutionApiConfigMutationVariables,
  useGetEvolutionApiInstanceStatusLazyQuery,
  useConnectOrDisconnectEvolutionApiMutation,
} from 'src/graphql/generated';

import { RHFSwitch, RHFTextField } from 'src/components/hook-form';

import {
  EvolutionApiFormSchema,
  evolutionApiConfigFormSchemaDefaultValues,
} from '../schema/evolution-api-form-schema';

type TAttributes = 'apiKey' | 'instance' | 'connected' | 'phoneNumber';

type TCurrentData = Pick<EvolutionApiIntegrationType, TAttributes>;

type Props = {
  open: boolean;
  onClose: () => void;
  disableForm?: boolean;
  currentData?: TCurrentData | null;
  refetch: () => Promise<ApolloQueryResult<any>>;
  title?: string;
};

export function ConfigEvolutionApiModal({
  open,
  onClose,
  disableForm,
  currentData,
  refetch,
  title,
}: Props) {
  const [enableForm, setEnableForm] = useState<boolean>(!disableForm);
  const [showQRCode, setShowQRCode] = useState<boolean>(false);
  const [qrCodeValue, setQrCodeValue] = useState<string>('');
  const [saveEvolutionApiConfig, { loading }] = useSaveEvolutionApiConfigMutation();
  const [connectOrDisconnect] = useConnectOrDisconnectEvolutionApiMutation();
  const [getEvolutionApiInstanceStatus] = useGetEvolutionApiInstanceStatusLazyQuery({
    fetchPolicy: 'network-only',
  });

  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(EvolutionApiFormSchema),
    defaultValues: evolutionApiConfigFormSchemaDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = methods;

  const handleClose = () => {
    onClose();
    reset();
    setEnableForm(!disableForm);
    setShowQRCode(false);
    setQrCodeValue('');
  };

  const onSubmit = handleSubmit(async (data) => {
    const { connected, phoneNumber = '' } = data;

    const variables: SaveEvolutionApiConfigMutationVariables = {
      input: { phoneNumber },
    };

    try {
      const { data: saveResponse } = await saveEvolutionApiConfig({ variables });

      const saveSuccess = saveResponse?.saveEvolutionApiIntegration?.ok;

      if (!saveSuccess) {
        enqueueSnackbar('Erro ao salvar os dados da integração', { variant: 'error' });
        return false;
      }

      const isCreated = saveResponse?.saveEvolutionApiIntegration?.isCreated;

      if (isCreated) {
        enqueueSnackbar('Cadastro efetuado com sucesso!', { variant: 'success' });
        await handleConnectionChange(true);
      } else if (currentData?.connected !== connected) {
        enqueueSnackbar('Dados atualizados com sucesso!', { variant: 'success' });
        await handleConnectionChange(connected ?? false);
      } else {
        enqueueSnackbar('Dados atualizados com sucesso!', { variant: 'success' });
        finalizeForm();
      }

      await refetch();
      return true;
    } catch (error: any) {
      enqueueSnackbar(error?.message ?? 'Não foi possível atualizar os dados!', {
        variant: 'error',
      });
      return false;
    }
  });

  const handleConnectionChange = async (connected: boolean) => {
    const { data: connectResponse } = await connectOrDisconnect({
      variables: { input: { connected } },
    });

    const result = connectResponse?.connectOrDisconnectEvolutionApiIntegration;

    if (!result?.ok) {
      enqueueSnackbar('Erro ao conectar ou desconectar a integração', { variant: 'error' });
      return false;
    }

    if (connected) {
      setShowQRCode(true);
      const { response } = result;
      setQrCodeValue(JSON.parse(response ?? '{}')?.base64 ?? '');
    } else {
      enqueueSnackbar('Integração desconectada com sucesso!', { variant: 'success' });
      setShowQRCode(false);
      setQrCodeValue('');
      finalizeForm();
    }
    return true;
  };

  const finalizeForm = () => {
    setEnableForm(false);
    onClose();
  };

  const [isRefreshingQR, setIsRefreshingQR] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  useEffect(() => {
    const refreshQRCode = async () => {
      if (isRefreshingQR) return;
      setIsRefreshingQR(true);
      try {
        const { data: connectResponse } = await connectOrDisconnect({
          variables: { input: { connected: true } },
          fetchPolicy: 'no-cache',
        });
        const result = connectResponse?.connectOrDisconnectEvolutionApiIntegration;
        if (result?.ok) {
          const { response } = result;
          const nextBase64 = JSON.parse(response ?? '{}')?.base64 ?? '';
          if (nextBase64 && nextBase64 !== qrCodeValue) {
            setQrCodeValue(nextBase64);
          }
        }
      } catch {
        enqueueSnackbar('Erro ao atualizar o QR Code', { variant: 'error' });
      } finally {
        setIsRefreshingQR(false);
      }
    };

    const checkInstanceStatus = async () => {
      if (isCheckingStatus) return;
      setIsCheckingStatus(true);
      try {
        const { data } = await getEvolutionApiInstanceStatus({ fetchPolicy: 'network-only' });
        const isConnected = data?.getEvolutionApiConnectionStatus?.equalStatus === true;
        if (isConnected) {
          enqueueSnackbar('Instância conectada com sucesso!', { variant: 'success' });
          setShowQRCode(false);
          setQrCodeValue('');
          await refetch();
          finalizeForm();
        }
      } catch {
        enqueueSnackbar('Erro ao checar status da instância', { variant: 'error' });
      } finally {
        setIsCheckingStatus(false);
      }
    };

    let qrInterval: ReturnType<typeof setInterval> | undefined;
    let statusInterval: ReturnType<typeof setInterval> | undefined;

    if (showQRCode) {
      refreshQRCode();
      checkInstanceStatus();

      qrInterval = setInterval(refreshQRCode, 15_000);
      statusInterval = setInterval(checkInstanceStatus, 5_000);
    }

    return () => {
      if (qrInterval) clearInterval(qrInterval);
      if (statusInterval) clearInterval(statusInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQRCode, qrCodeValue]);

  useEffect(() => {
    const updateFields = () => {
      if (!currentData) return;

      const { connected, phoneNumber } = currentData;

      reset({
        connected: connected ?? false,
        phoneNumber: phoneNumber ?? '',
      });
    };

    updateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  useEffect(() => {
    setEnableForm(!disableForm);
  }, [disableForm]);

  const renderQRCode = (qrCode: string) => {
    if (!showQRCode) return null;
    const showLoader = isRefreshingQR || !qrCode;
    return (
      <Stack sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <Typography variant="h6" textAlign="center">
          Escaneie o QR Code para conectar ao WhatsApp
        </Typography>
        {showLoader ? (
          <Stack sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">
              {isRefreshingQR ? 'Atualizando QR Code...' : 'Gerando QR Code...'}
            </Typography>
          </Stack>
        ) : (
          <img src={qrCode} alt="QR Code" style={{ width: 200, height: 200 }} />
        )}
        <Typography variant="caption">
          Conecte-se ao mesmo WhatsApp indicado na etapa anterior
        </Typography>
      </Stack>
    );
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', md: 600 },
          },
        }}
      >
        <DialogContent
          sx={{
            minWidth: { xs: 300, md: 600 },
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            marginBottom: 2,
            py: 2,
          }}
        >
          {showQRCode ? (
            renderQRCode(qrCodeValue)
          ) : (
            <>
              <DialogTitle textAlign="center" fontSize={20}>
                {title ?? 'Integração Evolution API'}
              </DialogTitle>

              <RHFTextField
                name="phoneNumber"
                label="Número de Telefone"
                disabled={!enableForm}
                helperText="Exemplo: 5511971048980 (código do país + DDD + número)"
                FormHelperTextProps={{
                  sx: {
                    fontWeight: 600,
                  },
                }}
              />
            </>
          )}
        </DialogContent>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <DialogActions
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {!showQRCode && (
            <RHFSwitch
              name="connected"
              label={watch('connected') ? 'Serviço Ativo' : 'Serviço Inativo'}
              disabled={!enableForm || !currentData}
            />
          )}
          <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              Fechar
            </Button>
            {enableForm && !showQRCode && (
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                loading={isSubmitting || loading}
                onClick={onSubmit}
              >
                Salvar
              </LoadingButton>
            )}
            {!enableForm && (
              <Button variant="contained" color="primary" onClick={() => setEnableForm(true)}>
                {currentData ? 'Editar' : 'Criar'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
